import { defineStore } from 'pinia';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { computed, h, reactive } from 'vue';
import { IParentBusiness, isIParentBusiness } from '../../domains/entities/Business';
import { GetMonthlyDataService } from '../../domains/services/getMonthlyDataService';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { simpleCopy } from '@/utils/deepCopy';
import { IInventoryRepository } from '@/coreInfrastructureModule/interfaces/IInventoryRepository';
import { IPagingOptions } from '@/coreDomainsModule/paging';
import { GetColumnWidthsUseCase } from '@/corePagesModule/BaseTableContent/useCases/getColumnWidthsUseCase';
import { TableCommonViewModel } from '@/viewModels/tableContent';
import { ForecastAccuracyAdapter, IForecastAccuracyRawData } from '../../adapters/ForecastAccuracyAdapter';
import { IArrival, OrderStatus } from '@/coreDomainsModule/entities/arrival';
import { IOrderRepository } from '@/coreInfrastructureModule/interfaces/IOrderRepository';
import { i18n } from '@/i18n';
import {
  openConfirmModal,
  openFailToSavedModal,
  openLoadingModal,
  openOkInformationModal,
  openSuccessToSaveModal,
} from '@/corePagesModule/components/modal/modalActions';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import { InventoryTableViewModel } from '../inventoryTableViewModel';
import { RecommendOrderFactory } from '../../domains/factories/RecommendOrderFactory';
import { GetRecommendedOrderForInventoryControlUseCase } from '../../useCases/InventorySimulationUseCase';
import { toast } from '@/corePagesModule/components/Toast/ToastManager';
import { AddAggregateBusinessGroupUseCase } from '@/corePagesModule/group/useCases/addAggregateBusinessGroupUseCase';
import { IGroupRepository } from '@/coreInfrastructureModule/interfaces/IGroupRepository';
import { CreateGroupUseCase } from '@/corePagesModule/group/useCases/createGroupUseCase';
import { DeleteGroupUseCase } from '@/corePagesModule/group/useCases/deleteGroupUseCase';
import { updateGroupNameUseCase } from '@/corePagesModule/group/useCases/updateGroupNameUseCase';
import { GroupMenuItems } from '../../domains/entities/GroupMenu';
import { DeleteAggregateBusinessGroupUseCase } from '@/corePagesModule/group/useCases/deleteAggregateBusinessGroupUseCase';
import { GroupMenuService } from '../../domains/services/groupMenuService';
import { AlertDataObject } from '@/coreDomainsModule/alert';
import { InventoryAlertKinds } from '../../domains/entities/Alert';
import { ParentBusinessWithMoveWarehouseFactory } from '../../domains/factories/ParentBusinessWithMoveWarehouseFactory';
import { IDate, IYearMonth } from '@/coreDomainsModule/date';
import { MoveWarehouseUseCase } from '../../useCases/moveWarehouseUseCase';
import { useTableAlert } from '../../composables/useTableAlert';
import { UpsertAndSingleSimulationOrderUseCase } from '@/coreUseCasesModule/order/upsertAndSingleSimulationOrderUseCase';
import { useTableCalculation } from '../../composables/useTableCalculation';
import { downloadExcelFromUrl } from './inventoryTemplate';
import { BusinessRowMenuState, IInventoryState } from './type';
import { initInventoryState, initStartAndEndYearMonth } from './initValue';
import { diContainer } from '@/coreContainer/container';
import { INetworkResult } from '@/coreDomainsModule/networkResult';
import {
  UpdateTrashBinAggregateBusinessSuccessResponseInfo,
  UpdateTrashBinBusinessSuccessResponseInfo,
} from '@/coreInfrastructureModule/axios/generated/trashBinManage';
import { IError } from '@/coreInfrastructureModule/interfaces/IError';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { YearMonthRange } from '@/coreDomainsModule/entities/yearMonthRange';
import { YearMonthRangeInfoRepository } from '@/coreInfrastructureModule/Repositories/YearMonthRangeInfoRepository';
import { useTableFilter } from '@/corePagesModule/composables/useTableFilter';
import { ArrivalFactory } from '@/coreDomainsModule/factories/ArrivalFactory';
import { IMonthlyBusinessData } from '@/InventoryControlModule/domains/entities/MonthlyData';
import { IInventoryOrder, IInventoryOrderInner } from '@/InventoryControlModule/domains/entities/InventoryOrder';
import { OrderBasisFactory } from '@/coreDomainsModule/factories/OrderBasisFactory';
import {
  loadMyPropertyPreferences,
  myPropertyPreferences,
  loadPreferences,
  myPreferences,
  savePreferences,
} from '@/appLogic/view/preferences';
import { OrderBasisService } from '@/coreDomainsModule/services/orderBasisService';
import { InventoryTableService } from '../../domains/services/inventoryTableService';

/**
 * 在庫管理画面のStoreを管理する
 */
export const useInventoryStore = defineStore('inventory', () => {
  const t = i18n.t;
  const vm = new TableCommonViewModel();
  const inventoryVm = new InventoryTableViewModel();
  const { getMonthlyData } = useTableCalculation();

  // アラート処理用のComposableを使用
  const { getTableAlertData: getTableAlertDataFromComposable, checkUnexpectedStatus, getOrderAmountForAlert } = useTableAlert();

  // 生データキャッシュ（APIレスポンスをキャッシュして無駄な通信を防ぐ）
  const rawDataCache = reactive<Record<string, IForecastAccuracyRawData>>({});

  const state = reactive<IInventoryState>(initInventoryState());

  // 現在のテーブルタイプを保持
  const whichTableDisplay = computed(() => (state.pageMode === 'order' ? 'inventoryControlWithOrder' : 'inventoryControl'));

  // フィルター情報のComposable
  const { cachedFilterItems, bulkUpdateFilterInfos, getFilterItemsValue, initTableFilter } = useTableFilter(whichTableDisplay.value, [
    whichTableDisplay.value === 'inventoryControlWithOrder' ? 'inventoryControl' : 'inventoryControlWithOrder',
  ]);

  /**
   * stateの初期化
   */
  const $reset = async () => {
    Object.assign(state, initInventoryState());
    await _initSelectedYearMonthRange();
  };

  /**
   * テーブルの列幅を初期化する
   */
  // TODO: useColumnResizerに移譲する
  const initTableColumnsWidth = async () => {
    const getColumnWidthsUseCase = new GetColumnWidthsUseCase();
    // 列幅及びヘッダーのリストをDBから取得
    const columnWidthResult = await getColumnWidthsUseCase.Execute(whichTableDisplay.value);
    if (columnWidthResult.status.type === 'success') {
      const columnWidths = columnWidthResult.status.data;
      // keyの追加などヘッダー配列に変更があった場合、保持されていた列幅は無視する
      for (const key of Object.keys(columnWidths)) {
        // テーブル全体の幅との比率で保持されているため、再計算してセットする
        state.tableInfos[key] = columnWidths[key];
      }
      getHiddenTransactionHeader();
    } else {
      // PreferenceTableの初回生成より後に実行されるため基本的にここには来ない想定
      // もし取得できなかった場合のためにconsole.error残しておきます
      console.error('ヘッダー情報取得エラー: 列幅情報が取得できていません。');
    }
  };

  /**
   * stateのFetchと初期化を行う
   */
  const fetchState = async () => {
    // テーブル表示情報の取得処理
    // ここまで来たら登録処理が走るので、ローディング開始
    state.tableLoadingState.status.type = 'loading';
    await initTableFilter();

    // 永続化された設定を読み込む
    await loadPreferences();
    const savedPreferences = myPreferences[whichTableDisplay.value];

    if (savedPreferences) {
      // ソート設定の適用
      if (savedPreferences.sortKey) {
        state.headerSortingState.key = savedPreferences.sortKey;
      }
      if (savedPreferences.isAsc !== undefined) {
        state.headerSortingState.isAsc = savedPreferences.isAsc;
      }

      // 表示件数設定の適用
      if (savedPreferences.pageSize) {
        if (state.pagingOptions) {
          state.pagingOptions.pageSize = savedPreferences.pageSize;
        }
      }
    }

    const tableData = await _getTableData();
    if (tableData == null) return;

    state.businessList = tableData;

    const monthlyDataBuffer: Record<string, IMonthlyBusinessData> = {};
    const orderEditDataBuffer: IInventoryOrder = {};

    state.businessList.forEach((business) => {
      monthlyDataBuffer[business.businessId] = GetMonthlyDataService.Execute(business, state.transactionMonths);
      orderEditDataBuffer[business.businessId] = getSelectedOrderInEditMode(business);
    });

    state.monthlyData = monthlyDataBuffer;
    state.orderEditData = orderEditDataBuffer;

    if (tableData.length) {
      state.tableLoadingState.status.type = 'success';
    } else {
      state.tableLoadingState.status.type = 'zeroMatch';
    }
  };

  /**
   * 対象の取引の小取引を展開するかどうかを切り替える
   * @param businessId - 取引ID
   */
  const toggleOpenBusinesses = async (businessId: string) => {
    // 開いている取引を閉じるだけの場合
    if (state.isOpeningBusinessId === businessId) {
      state.isOpeningBusinessId = '';
      return;
    }

    state.isOpeningBusinessId = businessId;

    await fetchChildBusinesses(businessId);
  };

  /**
   * 対象の取引の小取引を取得する
   * @param businessId - 取引ID
   */
  const fetchChildBusinesses = async (businessId: string) => {
    const targetBusiness = state.businessList.find((business) => business.businessId === businessId);
    if (targetBusiness === undefined) return;

    // childrenがすでに取得済みの場合は何もしない
    if (targetBusiness.childrenAsyncStatus === 'loaded') {
      return;
    }

    targetBusiness.childrenAsyncStatus = 'loading';

    // FIXME: これ、海外対応がどうなるのかが分からん(リージョンによって情報が変わるかも)
    // 期間情報の作成 FIXME: とりあえずtodayから固定で出しているが、そのうち選択UI作成して適応させる
    const earlyPeriod = DateService.convertDateStrToDateWithHyphen(
      new Date(
        state.selectDisplayInventoryYearMonthRangeModalState.startYearMonth.year,
        state.selectDisplayInventoryYearMonthRangeModalState.startYearMonth.month - 1,
        1
      )
    );
    const latterPeriod = DateService.convertDateStrToDateWithHyphen(
      new Date(
        state.selectDisplayInventoryYearMonthRangeModalState.endYearMonth.year,
        state.selectDisplayInventoryYearMonthRangeModalState.endYearMonth.month - 1,
        1
      )
    );

    const result = await diContainer.getInventoryBusinessUseCase.execute(
      businessId,
      'ASC',
      150,
      0,
      earlyPeriod,
      latterPeriod,
      cachedFilterItems.value
    );

    switch (result.status.type) {
      case 'success':
        targetBusiness.children = result.status.data;
        targetBusiness.children.forEach((childBusiness) => {
          state.monthlyData[childBusiness.businessId] = GetMonthlyDataService.Execute(childBusiness, state.transactionMonths);
        });
        targetBusiness.childrenAsyncStatus = 'loaded';
        return;
      case 'error':
      default:
        targetBusiness.childrenAsyncStatus = 'error';
        return;
    }
  };

  /**
   * メモ列の表示内容を更新する
   * @param businessId - 更新対象の取引ID
   * @param memoText - 反映するメモ本文（未入力時はundefined）
   * @param dataType - 集計取引か子取引かの種別
   */
  const updateMemoText = (businessId: string, memoText: string | undefined, dataType: 'aggregateBusiness' | 'business') => {
    if (dataType === 'aggregateBusiness') {
      const targetBusiness = state.businessList.find((business) => business.businessId === businessId);
      if (targetBusiness === undefined) return;
      targetBusiness.note = memoText;
      return;
    }

    for (const parentBusiness of state.businessList) {
      const childBusiness = parentBusiness.children.find((child) => child.businessId === businessId);
      if (childBusiness !== undefined) {
        childBusiness.note = memoText;
        return;
      }
    }
  };

  /**
   * ページング変更時の処理
   * @param pagingOptions - ページングオプション
   */
  const onChangePaging = async (pagingOptions: IPagingOptions) => {
    state.pagingOptions = {
      pageSize: pagingOptions.pageSize,
      offset: pagingOptions.offset,
      nextOffset: pagingOptions.nextOffset,
      totalResultsAvailable: pagingOptions.totalResultsAvailable,
      returnCount: pagingOptions.returnCount,
    };
    // 表示件数設定の永続化対応：IndexedDBに保存
    await loadPreferences();
    if (!myPreferences[whichTableDisplay.value]) {
      myPreferences[whichTableDisplay.value] = {};
    }
    myPreferences[whichTableDisplay.value].pageSize = state.pagingOptions.pageSize;
    await savePreferences();
    await fetchState();
  };

  /**
   * テーブルの通常モードと発注モードを切り替える
   */
  const switchTableMode = async () => {
    state.pageMode = state.pageMode === 'order' ? 'normal' : 'order';
    await initTableColumnsWidth();
  };

  /**
   * 画面モードを通常モードに設定する
   */
  const setNormalMode = () => {
    state.pageMode = 'normal';
  };

  /**
   * 発注モードで表示する発注を取得する
   * @param business - 対象の取引
   */
  const getSelectedOrderInEditMode = (business: IParentBusiness): { [key: number]: IInventoryOrderInner } => {
    const res: { [key: number]: IInventoryOrderInner } = {};
    for (const monthForCalc of state.transactionMonths) {
      // 1ヶ月前以降が対象
      if (monthForCalc < -1) continue;
      const monthForTarget = monthForCalc === -1 ? 0 : monthForCalc;
      // recommendがない場合は初期値入力
      if ((business.recommend === undefined || business.recommend.length === 0) && res[monthForTarget] === undefined) {
        res[monthForTarget] = RecommendOrderFactory.create({
          amount: NaN,
          crossDate: DateService.convertDateToIDate(new Date()),
        });
        continue;
      }
      // 対象月のrecommendから承認依頼発注を取得し、設定する
      const approvalRequestedOrder = business.recommend.find((recommend) => {
        return (
          recommend.status === OrderStatus.ApprovalRequested &&
          recommend.crossDate !== undefined &&
          DateService.compareYearMonths(recommend.crossDate, DateService.addMonthsToCurrentDate(monthForCalc)) === 0
        );
      });
      if (approvalRequestedOrder !== undefined) {
        res[monthForTarget] = approvalRequestedOrder;
        continue;
      }

      // 承認依頼発注が存在しない場合、対象月のrecommendから推奨発注を取得し、設定する
      const recommendedOrder = business.recommend.find((recommend) => {
        return (
          (recommend.status === OrderStatus.Recommended || recommend.status === undefined) && // NOTE: recommend.statusがundefinedの場合も推奨発注として扱う
          recommend.crossDate !== undefined &&
          DateService.compareYearMonths(recommend.crossDate, DateService.addMonthsToCurrentDate(monthForCalc)) === 0
        );
      });
      if (recommendedOrder !== undefined) {
        res[monthForTarget] = { ...recommendedOrder, status: OrderStatus.Recommended };
        continue;
      }

      // どちらも存在しない場合は初期値入力
      if (res[monthForTarget] !== undefined) continue;
      res[monthForTarget] = RecommendOrderFactory.create({
        amount: NaN,
        crossDate: DateService.convertDateToIDate(new Date()),
      });
    }
    return res;
  };

  /**
   * 指定した取引を一意に特定するためのフィルター一覧を生成する
   * @param business - 対象の取引
   * @returns フィルター条件の配列
   */
  const createFilterItemsForBusiness = (business: IParentBusiness): FilterContentItem[] => {
    const filters: FilterContentItem[] = [];
    /**
     * フィルター配列へ値を追加する
     * @param filterType - フィルター種別
     * @param value - フィルター値
     */
    const pushFilter = (filterType: FilterContentItem['filterType'], value?: string | number | null) => {
      if (value === undefined || value === null || value === '') return;
      filters.push({ filterType, selectedValue: [String(value)], label: '' });
    };

    pushFilter('SUPPLIER_NAME', business.supplierName);
    pushFilter('SUPPLIER_CODE', business.supplierNumber);
    pushFilter('BUSINESS_CODE', business.businessNumber);
    pushFilter('BUSINESS_NAME', business.businessName);
    pushFilter('PACKAGE_AMOUNT', business.package);
    pushFilter('WAREHOUSE', business.warehouse);
    return filters;
  };

  /**
   * 指定した取引の情報を再取得してstateへ反映する
   * @param businessId - 再取得する取引のID
   */
  const refreshBusiness = async (businessId: string) => {
    const targetBusiness = state.businessList.find((business) => business.businessId === businessId);
    if (!targetBusiness) {
      throw new Error('Target business not found');
    }

    const earlyPeriod = DateService.convertDateStrToDateWithHyphen(
      new Date(
        state.selectDisplayInventoryYearMonthRangeModalState.startYearMonth.year,
        state.selectDisplayInventoryYearMonthRangeModalState.startYearMonth.month - 1,
        1
      )
    );
    const latterPeriod = DateService.convertDateStrToDateWithHyphen(
      new Date(
        state.selectDisplayInventoryYearMonthRangeModalState.endYearMonth.year,
        state.selectDisplayInventoryYearMonthRangeModalState.endYearMonth.month - 1,
        1
      )
    );

    const result = await diContainer.getInventoryUseCase.execute(
      state.headerSortingState.isAsc ? 'ASC' : 'DESC',
      1,
      0,
      earlyPeriod,
      latterPeriod,
      state.headerSortingState.key,
      createFilterItemsForBusiness(targetBusiness)
    );

    if (result.status.type !== 'success') {
      throw result.status.error ?? new Error('Failed to refresh business');
    }

    const [businesses] = result.status.data;
    const businessArray = simpleCopy(businesses ?? []);
    const simulateResult = await GetRecommendedOrderForInventoryControlUseCase.Execute(businessArray);

    if (simulateResult.status.type !== 'success' || simulateResult.status.data.length === 0) {
      throw new Error('Failed to rebuild business data');
    }

    const refreshedBusiness = simulateResult.status.data[0];
    const mergedBusiness: IParentBusiness = {
      ...targetBusiness,
      ...refreshedBusiness,
      children: targetBusiness.children,
      childrenAsyncStatus: targetBusiness.childrenAsyncStatus,
    };

    state.orderEditData[mergedBusiness.businessId] = getSelectedOrderInEditMode(mergedBusiness);

    const index = state.businessList.findIndex((business) => business.businessId === businessId);
    if (index !== -1) {
      state.businessList.splice(index, 1, mergedBusiness);
    }

    state.monthlyData[businessId] = GetMonthlyDataService.Execute(mergedBusiness, state.transactionMonths);
  };

  /**
   * 発注済み未納に関するInfoModalを開く
   * @param executionDate - ExecutionDate
   * @param unpaidOrderedOrder - 発注済み未納
   */
  const _openUnpaidOrderedOrderInfoModal = async (executionDate: Date, unpaidOrderedOrder: IArrival[]) => {
    let isCanceled = false;
    const unpaidOrderedOrderInfoModalResult = await showInfoModal({
      modalTitle: t('発注済み未納が存在します'),
      modalDescription: `${t('最新の在庫に反映されていない場合、誤った推奨発注を表示している場合があります。')}${t('続行しますか？')}`,
      // eslint-disable-next-line jsdoc/require-jsdoc
      modalFooter: ({ onBack }) =>
        h(
          'button',
          {
            style: {
              color: '#1e92ef',
              borderBottom: '1px solid',
            },
            // eslint-disable-next-line jsdoc/require-jsdoc
            onClick: () => {
              isCanceled = true;
              onBack();
            },
          },
          t('キャンセル')
        ),
      backButtonLabel: t('続行する'),
      nextButtonLabel: t('発注済み未納を確認する'),
      icon: 'info',
      colorStyle: 'primary',
      enableOutsideClick: false,
      modalWidth: 764,
    });

    if (unpaidOrderedOrderInfoModalResult) {
      // 発注済み未納確認
      // モーダル展開
      state.editUnpaidOrderedOrderModalState.executionDate = executionDate;
      state.editUnpaidOrderedOrderModalState.unpaidOrderedOrder = unpaidOrderedOrder;
      state.editUnpaidOrderedOrderModalState.isOpening = true;
      return false;
    } else if (!isCanceled) {
      // 発注推奨確定時でモーダル続行押下
      return true;
    } else {
      // キャンセル
      return false;
    }
  };

  /**
   * a
   * @param business - a
   */
  const _getUnpaidOrderedOrder = (business: IParentBusiness): IArrival[] => {
    /**
     * 日付を比較用の数値に変換する
     * @param date - 変換したい日付(IArrival | IDate)
     * @returns 比較用の数値(YYYYMMDDの8桁)
     */
    const getEvalValue = (date: IArrival | IDate): number => {
      if (!date) {
        return 0;
      }
      return date.year * 10000 + date.month * 100 + date.day;
    };
    const executionDate = business.executionDate;
    if (!executionDate) {
      // executionDateがないのはありえないが、もしなかったら空の配列を返す
      return [];
    }
    const output = simpleCopy(business.recommend)
      ?.filter((recommendation) => {
        const arrivalDate = recommendation.crossDate;
        // 分納がある場合、分納取引のうち1つでも条件に引っかかっていたらTrueにする
        if (recommendation.status === OrderStatus.PartialDelivery && recommendation.partialDeliveries) {
          return recommendation.partialDeliveries.some(
            (pd) =>
              pd.crossDate &&
              getEvalValue(pd.crossDate) <= getEvalValue(DateService.convertDateToIDate(executionDate)) &&
              pd.status !== OrderStatus.Delivered &&
              pd.status !== OrderStatus.DisplayEnded
          );
        }
        // ArrivalDateが存在し、executionDate以前であり、かつ納入済み、納入遅延、表示終了のステータスでないものを取得する
        return (
          arrivalDate &&
          getEvalValue(arrivalDate) <= getEvalValue(DateService.convertDateToIDate(executionDate)) &&
          recommendation.status !== OrderStatus.Delivered &&
          recommendation.status !== OrderStatus.DisplayEnded
        );
      })
      .map((o) => {
        return ArrivalFactory.create({ ...o, number: o.orderNumber });
      });
    return output;
  };

  /**
   * 入力した値を元に発注情報の保存を行う
   * @param orderRepository - 発注情報の永続化の責務を負うクラス
   * @param business - 対象取引
   * @param month - 対象月
   */
  const confirmOrderEdit = async (orderRepository: IOrderRepository, business: IParentBusiness, month: number) => {
    if (state.orderEditData[business.businessId] === undefined) return;
    if (state.orderEditData[business.businessId][month] === undefined) return;

    // 発注済み未納バリデーション
    await loadMyPropertyPreferences();
    const unpaidOrderedOrder = _getUnpaidOrderedOrder(business);

    if (myPropertyPreferences.order.unpaidOrderedOrderAppearance && unpaidOrderedOrder.length > 0) {
      if (!business.executionDate) return;
      const confirmHasUnpaidOrderedOrder = await _openUnpaidOrderedOrderInfoModal(business.executionDate, unpaidOrderedOrder);
      if (!confirmHasUnpaidOrderedOrder) return;
    }

    const modalResult = await openConfirmModal(
      'question',
      t('発注の確定(詳細ページ)'),
      t('確定'),
      t('キャンセル'),
      undefined,
      t('発注確定テキスト'),
      {
        useKeyboard: true,
      }
    );

    if (!modalResult.isConfirmed) return;

    const orderData = state.orderEditData[business.businessId][month];

    if (!Number.isFinite(orderData.amount)) {
      await openOkInformationModal('error', t('エラー'), 'OK', t('半角で数値を入力してください'));
      return;
    }

    orderData.isLoading = true;

    // 発注更新機能のローディングステータス
    state.editOrderInfoLoadingState.status.type = 'loading';

    // 新規追加の発注かどうかを判定フラグ
    const isNewOrder = orderData.status === undefined;

    // 発注情報を承認済（納期回答待ち）に昇格する
    orderData.status = OrderStatus.ApprovedAwaitingDeliveryDate;

    // IOrderBasisの情報を作成する
    const createdOrderBasisInfo = OrderBasisFactory.create({
      snapshotLatestInventoryAmount: business.inventory,
      snapshotExecutionDate: business.executionDate?.getTime(),
      snapshotPackage: Number(business.package),
      snapshotMoq: business.MOQ,
      userAdjustedLeadTime:
        business.leadTime && business.leadTimeUnit ? { value: business.leadTime, unit: business.leadTimeUnit } : undefined,
      userAdjustedStdInventoryIndex: business.stdInventoryIndices?.['標準在庫指数'],
      nextMonthlyInfos: OrderBasisService.getNextMonthlyInfosForCreateOrder(
        business.customerAnnounce ?? [],
        (business.customerAnnounce ?? []).map((cf) => ({
          ...cf,
          amount: (cf.amount * (state.forecastAccuracyData[business.businessId]?.settingAccuracy || 100)) / 100,
        })),
        business.recommend,
        business,
        [orderData]
      ),
      pastMonthlyInfos: OrderBasisService.getPastMonthlyInfosForCreateOrder(business.customerAnnounce ?? [], business.salesResults ?? []),
    });

    // 保存
    // STOPSHIP: DIContainerの移行対応時に対応を行う必要あり
    const upsertOrderUseCase = new UpsertAndSingleSimulationOrderUseCase(orderRepository);
    const result = await upsertOrderUseCase.Execute(orderData, business.businessId, createdOrderBasisInfo);

    switch (result.status.type) {
      case 'success':
        // NOTE: 登録されると発注IDが返却されることを想定してるが安全のためチェック
        if (result.status.data.orderId)
          // arrivalIdをセットする
          orderData.arrivalId = result.status.data.orderId;
        // DBに登録したのでorderDataのisFixOrderedをTrueにする
        orderData.isFixOrdered = true;
        // OrderStatusがundefinedの場合は、新規追加の発注のため取引情報に追加する
        if (isNewOrder) business.recommend.push(orderData);
        // 確定した発注データ(orderData)を昇格する
        else
          business.recommend = business.recommend.map((recommend) => {
            if (recommend.displayId === orderData.displayId) {
              return orderData;
            }
            return recommend;
          });
        // 削除後のデータ内に　Recommended　と　ApprovalRequested　が存在するかを確認する
        const isExistRecommendedOrApprovalRequested = business.recommend.some(
          (recommend) => recommend.status === OrderStatus.Recommended || recommend.status === OrderStatus.ApprovalRequested
        );
        // 取引情報に推奨情報を反映する
        business.recommendEstimateTime = result.status.data.recommendEstimateTime
          ? DateService.convertUnixTimeToDate(result.status.data.recommendEstimateTime / 1000)
          : undefined;
        business.recommendArrivalValue = result.status.data.recommendArrivalValue;

        if (!isExistRecommendedOrApprovalRequested) {
          // シミュレーションを回す
          const simulateResult = await GetRecommendedOrderForInventoryControlUseCase.Execute([business]);
          switch (simulateResult.status.type) {
            case 'success':
              // シミュレーション結果を取得する
              const [simulatedData] = simulateResult.status.data;
              // シミュレーション結果を取引情報に反映する
              state.businessList = state.businessList.map((business) => {
                if (business.businessId === simulatedData.businessId) {
                  return simulatedData;
                }
                return business;
              });
              business = simulatedData;
              break;
            case 'error':
            default:
              break;
          }
        }

        // 次の月の発注情報を設定する
        state.orderEditData[business.businessId] = getSelectedOrderInEditMode(business);
        // 月次データを更新する
        state.monthlyData[business.businessId] = GetMonthlyDataService.Execute(business, state.transactionMonths);

        state.editOrderInfoLoadingState.status.type = 'success';
        await openSuccessToSaveModal();
        break;
      case 'error':
        state.editOrderInfoLoadingState.status.type = 'error';
      default:
        state.editOrderInfoLoadingState.status.type = 'error';
        await openFailToSavedModal();
        break;
    }
    orderData.isLoading = false;
  };

  /**
   * 発注済み未納を一括更新する
   * @param editedOrders - a
   */
  const updateUnpaidOrderedOrder = async (editedOrders: IArrival[]) => {
    const result = await waitLoading(diContainer.bulkPutUnpaidOrderedOrderUseCase.execute(editedOrders));
    switch (result?.status.type) {
      case 'success':
        const resultData = result.status.data;
        if (resultData.notUpdatedOrderId.length > 0) {
          console.error('発注済み未納一括編集一部競合');
          await showInfoModal({
            modalTitle: t('一部の発注済み未納の編集に失敗しました'),
            modalDescription: t('一部の発注済み未納の編集に失敗しました。時間をおいて再度お試しください。'),
            backButtonLabel: t('OK'),
            isDisabledNextButton: true,
            enableOutsideClick: false,
          });
        } else {
          await showInfoModal({
            modalTitle: t('発注済み未納の編集に成功しました'),
            modalDescription: '',
            backButtonLabel: t('OK'),
            isDisabledNextButton: true,
            enableOutsideClick: false,
          });
        }
        state.editUnpaidOrderedOrderModalState.executionDate = undefined;
        state.editUnpaidOrderedOrderModalState.unpaidOrderedOrder = [];
        state.editUnpaidOrderedOrderModalState.isOpening = false;
        await fetchState();
        return;
      case 'error':
      default:
        console.error('発注済み未納一括編集に失敗');
        await showInfoModal({
          modalTitle: t('発注済み未納の編集に失敗しました'),
          modalDescription: t('発注済み未納の編集に失敗しました。リロードして再度お試しください。'),
          backButtonLabel: t('OK'),
          isDisabledNextButton: true,
          enableOutsideClick: false,
        });
    }
  };

  /**
   * 稼働日カレンダー未入力警告モーダルを非表示
   */
  const closeWorkDaysCalendarNotYetEnteredAlertModal = () => {
    state.workDaysCalendarNotYetEnteredAlertModalStatus = false;
  };

  /**
   * フィルターモーダル適用ボタンクリック時の処理
   * @param filterItems - フィルターモーダル内で選択された項目
   */
  const onAppliedFilterModal = async (filterItems: FilterContentItem[]) => {
    state.isOpeningBusinessId = '';
    await bulkUpdateFilterInfos(simpleCopy(filterItems));
    await fetchState();
  };

  /**
   * ヘッダー編集モーダル表示/非表示操作時の処理
   * @param key - 削除するヘッダーのkey
   * @param isHide - 非表示にさせるか否か
   */
  // TODO: 不要メソッドのため削除
  const onToggleDisplayHeader = async (key: string, isHide: boolean) => {
    //await _updateDisplayHeader(key, isHide);
    //await initTableColumnsWidth();
  };

  /**
   * TransactionHeaderの表示状態を取得する
   * @param key - トランザクションヘッダーキー
   * @param month - トランザクションヘッダーの月
   */
  const getIsTransactionHeaderDisplay = (key: string, month: number): boolean => {
    if (!inventoryVm.isTransactionKeys(key) && !inventoryVm.isTransactionKeysForPreOrderMode(key)) {
      console.error('ERROR', key, month);
      return false;
    }
    return !state.hiddenTransactionHeaderList.includes(inventoryVm.getArrivalHeaderKey(key, month));
  };

  /**
   * 全選択用のチェックボックス選択時の処理
   */
  const onCheckedAllRows = () => {
    for (const business of state.businessList) {
      toggleCheckedRowData(business, !state.isCheckAllRows);
      // 子取引が存在する場合、子取引も選択状態を変更する
      // TODO: 現在は展開表示されていない子取引もデータとして実装になってるかどうか確認する。（もし持ってる場合はそれも更新してしまうことになる）
      // 250710現在子取引の選択は未実装
      /*if (isIParentBusiness(business) && business.children.length > 0) {
        for (const childBusiness of business.children) {
          _toggleCheckedRowData(childBusiness, !state.isCheckAllRows);
        }
      }*/
    }
    state.isCheckAllRows = !state.isCheckAllRows;
  };

  // ↓ グループ関連

  /**
   * グループメニューが選択された際の処理
   * @param groupMenu - 選択されたグループメニュー
   */
  const onSelectedGroupMenu = (groupMenu: GroupMenuItems) => {
    state.selectedGroupMenu = groupMenu;
  };

  /**
   * グループ関連のモーダルが閉じられた際の処理
   */
  const onClosedGroupActionModal = () => {
    state.selectedGroupMenu = undefined;
  };

  /**
   * グループ新規作成を適用した際の処理
   * @param groupRepository - グループ情報の永続化の責務を負うクラス
   * @param groupName - グループ名
   */
  const onAppliedCreateAggregateBusinessGroup = async (groupRepository: IGroupRepository, groupName: string) => {
    // メニューの選択を解除
    state.selectedGroupMenu = undefined;

    // ローディング開始
    state.groupLoadingState.state.status.type = 'loading';
    state.groupLoadingState.type = 'create';

    // グループ作成処理
    const useCase = new CreateGroupUseCase();
    const result = await useCase.ExecuteMock(groupRepository, groupName);

    switch (result.status.type) {
      case 'success':
        // ローディング終了
        state.groupLoadingState.state.status.type = 'success';
        state.groupLoadingState.type = 'none';
        // 成功トーストを表示
        toast.success(t('グループ作成に成功しました。'));
        break;
      case 'error':
      default:
        state.groupLoadingState.state.status.type = 'error';
        state.groupLoadingState.type = 'none';
        // 失敗トーストを表示
        toast.error(t('グループ作成に失敗しました。'));
        break;
    }
  };

  /**
   * グループ追加を適用した際の処理
   * @param groupRepository - グループ情報の永続化の責務を負うクラス
   * @param targetGroupId - 追加対象のグループID
   */
  const onAppliedAddAggregateBusinessGroup = async (groupRepository: IGroupRepository, targetGroupId: string) => {
    // メニューの選択を解除
    state.selectedGroupMenu = undefined;

    // ローディング開始
    state.groupLoadingState.state.status.type = 'loading';
    state.groupLoadingState.type = 'addBusiness';

    // 追加処理
    const useCase = new AddAggregateBusinessGroupUseCase();
    const result = await useCase.ExecuteMock(groupRepository, state.checkedAggregateBusinessIds, targetGroupId);

    switch (result.status.type) {
      case 'success':
        // グループ用のローディング終了
        state.groupLoadingState.state.status.type = 'success';
        state.groupLoadingState.type = 'none';
        // 成功トーストを表示
        toast.success(t('グループへの追加に成功しました。'));
        // グループ追加後のデータを取得
        await fetchState();
        // チェック状態をリセット
        state.checkedAggregateBusinessIds = [];
        break;
      case 'error':
      default:
        state.groupLoadingState.state.status.type = 'error';
        state.groupLoadingState.type = 'none';
        // 失敗トーストを表示
        toast.error(t('グループへの追加に失敗しました。'));
        break;
    }
  };

  /**
   * グループ編集を適用した際の処理
   * @param inventoryRepository - 在庫情報の永続化の責務を負うクラス
   * @param selectedGroupId - 編集対象のグループID
   * @param selectedGroupName - 編集対象のグループ名
   */
  const onAppliedEditAggregateBusinessGroup = async (
    inventoryRepository: IInventoryRepository,
    selectedGroupId: string,
    selectedGroupName: string
  ) => {
    // メニューの選択を解除
    state.selectedGroupMenu = undefined;

    state.editGroupModeState.selectedEditGroupId = selectedGroupId;
    state.editGroupModeState.selectedEditGroupName = selectedGroupName;

    // 編集モードに遷移
    state.pageMode = 'editGroup';

    // チェック状態をリセット
    state.checkedAggregateBusinessIds = [];

    await fetchState();
  };

  /**
   * グループ削除を適用した際の処理
   * @param inventoryRepository - 在庫情報の永続化の責務を負うクラス
   * @param groupRepository - グループ情報の永続化の責務を負うクラス
   * @param targetGroupId - 削除対象のグループID
   */
  const onAppliedDeleteAggregateBusinessGroup = async (
    inventoryRepository: IInventoryRepository,
    groupRepository: IGroupRepository,
    targetGroupId: string
  ) => {
    // メニューの選択を解除
    state.selectedGroupMenu = undefined;

    // ローディング開始
    state.groupLoadingState.state.status.type = 'loading';
    state.groupLoadingState.type = 'delete';

    // 削除処理
    const useCase = new DeleteGroupUseCase();
    const result = await useCase.ExecuteMock(groupRepository, targetGroupId);

    switch (result.status.type) {
      case 'success':
        // グループ用のローディング終了
        state.groupLoadingState.state.status.type = 'success';
        state.groupLoadingState.type = 'none';
        // 成功トーストを表示
        toast.success(t('グループ削除に成功しました。'));
        // グループ削除後のデータを取得
        await fetchState();
        break;
      case 'error':
      default:
        state.groupLoadingState.state.status.type = 'error';
        state.groupLoadingState.type = 'none';
        // 失敗トーストを表示
        toast.error(t('グループ削除に失敗しました。'));
        break;
    }
  };

  /**
   * グループ名編集を適用した際の処理
   * @param groupRepository - グループ情報の永続化の責務を負うクラス
   * @param newGroupName - 編集後のグループ名
   */
  const onAppliedUpdateNameAggregateBusinessGroup = async (groupRepository: IGroupRepository, newGroupName: string) => {
    // モーダルを閉じる
    state.editGroupModeState.updateGroupNameModalState = false;

    if (state.editGroupModeState.selectedEditGroupId === '') {
      // エラートーストを表示
      toast.error(t('グループ編集に失敗しました。'));
      return;
    }

    // ローディング開始
    state.groupLoadingState.state.status.type = 'loading';
    state.groupLoadingState.type = 'updateName';

    // 編集処理
    const useCase = new updateGroupNameUseCase();
    const result = await useCase.ExecuteMock(groupRepository, state.editGroupModeState.selectedEditGroupId, newGroupName);

    switch (result.status.type) {
      case 'success':
        // ローディング終了
        state.groupLoadingState.state.status.type = 'success';
        state.groupLoadingState.type = 'none';
        // 編集が成功したとき、リクエストで投げた値をモーダルのフォームにセットする
        state.editGroupModeState.selectedEditGroupName = newGroupName;
        // 成功トーストを表示
        toast.success(t('グループ編集に成功しました。'));
        break;
      case 'error':
      default:
        state.groupLoadingState.state.status.type = 'error';
        state.groupLoadingState.type = 'none';
        // 失敗トーストを表示
        toast.error(t('グループ編集に失敗しました。'));
        break;
    }
  };

  //  ↓ グループ編集モード

  /**
   * グループ編集モードでグループを選択した際の処理
   * @param groupId - 選択されたグループID
   */
  const onSwitchedEditTargetGroup = async (groupId?: string) => {
    if (groupId === undefined) return;
    state.editGroupModeState.selectedEditGroupId = groupId;
    // 情報を再取得
    await fetchState();
  };

  /**
   * グループ編集モードでグループから外すボタンが押された際の処理
   * @param groupRepository - グループ情報の永続化の責務を負うクラス
   */
  const onClickedDeleteFromGroupButton = async (groupRepository: IGroupRepository) => {
    if (state.editGroupModeState.selectedEditGroupId === '' || state.checkedAggregateBusinessIds.length === 0) return;

    // ローディング開始
    state.groupLoadingState.state.status.type = 'loading';
    state.groupLoadingState.type = 'deleteBusiness';

    const useCase = new DeleteAggregateBusinessGroupUseCase();
    const result = await useCase.ExecuteMock(
      groupRepository,
      state.checkedAggregateBusinessIds,
      state.editGroupModeState.selectedEditGroupId
    );

    switch (result.status.type) {
      case 'success':
        // グループ用のローディング終了
        state.groupLoadingState.state.status.type = 'success';
        state.groupLoadingState.type = 'none';
        // 成功トーストを表示
        toast.success(t('グループから取引を外しました。'));
        // グループ削除後のデータを取得
        await fetchState();
        // チェック状態をリセット
        state.checkedAggregateBusinessIds = [];
        break;
      case 'error':
      default:
        state.groupLoadingState.type = 'none';
        // 失敗トーストを表示
        toast.error(t('グループから取引を外せませんでした。'));
        break;
    }
  };

  /**
   * グループ編集モードから通常モードに戻る際の処理
   */
  const onBackToNormalMode = async () => {
    // ページモードを通常モードに戻す
    setNormalMode();
    // 選択中のグループIDをリセット
    state.editGroupModeState.selectedEditGroupId = '';
    // チェック状態をリセット
    state.checkedAggregateBusinessIds = [];
    // 情報を再取得
    await fetchState();
  };

  //  ↑ グループ編集モード

  // ↑ グループ関連

  /**
   * アラートのデータを取得する
   * 新しいuseTableAlertコンポーザブルを使用してアラート処理を実行
   * @param business - businessData
   * @param monthDiff - 月の差分
   * @param kind - フィルタしたいアラートのタイプ（オプション）
   */
  const getTableAlertData = (business: IBusiness, monthDiff: number, kind?: InventoryAlertKinds): AlertDataObject[] => {
    // 新しいComposableの関数を使用し、月次データキャッシュを渡す
    return getTableAlertDataFromComposable(state.monthlyData, business, monthDiff, kind);
  };

  /**
   * 対象の取引のアラートモーダルを開くかどうかを切り替える
   * @param businessId - 取引ID
   * @param isOpening - 開いたかどうか
   */
  const toggleOpenAlert = async (businessId: string, isOpening: boolean) => {
    if (isOpening) {
      state.isOpeningAlertBusinessId = businessId;
    }
    return;
  };

  /**
   * 置き場移動に関連するstateをセットアップする
   * @param inventoryRepository - 在庫情報の永続化の責務を負うクラス
   * @param business - 取引
   * @param targetYearMonth - 対象年月
   */
  const setupMoveWarehouse = async (
    inventoryRepository: IInventoryRepository,
    business: IParentBusiness,
    targetYearMonth: IYearMonth
  ): Promise<void> => {
    // フィルター情報を作成
    /**
     * フィルター情報は、以下の5つ
     * 1. 置場名
     * 2. 仕入先名
     * 3. 商品CD
     * 4. 商品名
     * 5. 容量
     */
    const filterItems: FilterContentItem[] = [
      {
        filterType: 'SUPPLIER_NAME',
        selectedValue: [business.supplierName],
        label: '',
      },
      {
        filterType: 'BUSINESS_CODE',
        selectedValue: [business.businessNumber],
        label: '',
      },
      {
        filterType: 'PACKAGE_AMOUNT',
        selectedValue: [business.package],
        label: '',
      },
    ];

    state.moveWarehouseModalState.loadingStatus.status.type = 'loading';

    state.moveWarehouseModalState.changedBusiness = ParentBusinessWithMoveWarehouseFactory.createFromIParentBusiness(
      business,
      targetYearMonth
    );

    const earlyPeriod = DateService.convertDateStrToDateWithHyphen(new Date(targetYearMonth.year, targetYearMonth.month - 2, 1));
    const latePeriod = DateService.convertDateStrToDateWithHyphen(new Date(targetYearMonth.year, targetYearMonth.month + 1, 1));

    const result = await diContainer.getInventoryUseCase.execute('ASC', 20, 0, earlyPeriod, latePeriod, 'aggregateBusinessId', filterItems);

    switch (result.status.type) {
      case 'success':
        const [businesses] = result.status.data;
        state.moveWarehouseModalState.otherBusinesses = businesses.map((business) =>
          ParentBusinessWithMoveWarehouseFactory.createFromIParentBusiness(business, targetYearMonth)
        );
        if (state.moveWarehouseModalState.otherBusinesses.length === 0) {
          // NOTE: 置き場移動のアラートが発生している場合、ここに到達するのは理論上あり得ない（不具合もしくは入力ミスの可能性を疑う）
          state.moveWarehouseModalState.loadingStatus.status.type = 'zeroMatch';
          break;
        }
        // 同じ置き場コードの取引を除外
        state.moveWarehouseModalState.otherBusinesses = state.moveWarehouseModalState.otherBusinesses.filter(
          (business) => business.businessId !== state.moveWarehouseModalState.changedBusiness?.businessId
        );
        state.moveWarehouseModalState.loadingStatus.status.type = 'success';
        break;
      case 'error':
      default:
        state.moveWarehouseModalState.loadingStatus.status.type = 'error';
        break;
    }
    state.moveWarehouseModalState.isLoadingModalOpen = false;
    state.moveWarehouseModalState.isOpen = true;
  };

  /**
   * 置き場移動を適用する
   * @param inventoryRepository - 在庫情報の永続化の責務を負うクラス
   * @param businessIds - 変更する取引ID
   */
  const applyMoveWarehouse = async (inventoryRepository: IInventoryRepository, businessIds: string[]) => {
    // 何も選択されていない場合は何もしない
    if (businessIds.length === 0) return;

    // 置き場移動モーダルを閉じ、ローディングを開始
    state.moveWarehouseModalState.isOpen = false;
    state.moveWarehouseModalState.isLoadingModalOpen = true;

    if (state.moveWarehouseModalState.changedBusiness === undefined) {
      openFailToSavedModal();
      console.error('changedBusiness（変更される取引）がundefinedです');
      return;
    }

    const selectedBusiness = state.moveWarehouseModalState.otherBusinesses.filter((business) => businessIds.includes(business.businessId));

    const result = await MoveWarehouseUseCase.execute(inventoryRepository, state.moveWarehouseModalState.changedBusiness, selectedBusiness);
    switch (result.status.type) {
      case 'success':
        state.moveWarehouseModalState.isLoadingModalOpen = false;
        state.moveWarehouseModalState.isSuccessModalOpen = true;
        await fetchState();
        break;
      case 'error':
      default:
        state.moveWarehouseModalState.isLoadingModalOpen = false;
        openFailToSavedModal();
        break;
    }
  };

  /**
   * ヘッダーソート変更時の処理
   * @param key - ヘッダーキー
   */
  const changeHeaderSorting = async (key: string) => {
    state.headerSortingState.isAsc = key === state.headerSortingState.key ? !state.headerSortingState.isAsc : false;
    state.headerSortingState.key = key;

    // ソート設定の永続化対応：IndexedDBに保存
    await loadPreferences();
    if (!myPreferences[whichTableDisplay.value]) {
      myPreferences[whichTableDisplay.value] = {};
    }
    myPreferences[whichTableDisplay.value].sortKey = state.headerSortingState.key;
    myPreferences[whichTableDisplay.value].isAsc = state.headerSortingState.isAsc;
    await savePreferences();

    state.isOpeningBusinessId = '';
    await fetchState();
  };

  /**
   * 在庫表をExcelでエクスポートする処理
   */
  const exportInventoryBusiness = async () => {
    /**
     * エクスポート処理を実行する
     */
    const executeExport = async () => {
      return await diContainer.exportInventoryExcelUseCase.execute([], [], { year: 2024, month: 1 }, { year: 2024, month: 12 }, 'byMonth');
    };
    const result = await openLoadingModal(t('出力中'), executeExport, []);
    switch (result.status.type) {
      case 'success':
        const downloadUrl = result.status.data.download_url;
        const fileName = result.status.data.file_name || '在庫管理.xlsx';
        downloadExcelFromUrl(downloadUrl, fileName);
        break;
      case 'error':
      default:
        await openOkInformationModal('error', t('エラー'), 'OK', result.status.error.message);
        break;
    }
  };

  /**
   * 取引情報と開いている子取引があればそれも取得する関数
   */
  const fetchStateWithOpeningChildBusiness = async () => {
    await fetchState();
    if (state.isOpeningBusinessId) {
      await fetchChildBusinesses(state.isOpeningBusinessId);
    }
  };

  /**
   * TableBusinessRowMenuStateをリセットする関数
   */
  const resetTableBusinessRowMenuState = () => {
    state.businessRowMenuState = {
      businessId: '',
      item: undefined,
      businessType: 'none',
      edit: {
        isEditingModalOpen: false,
        isCreateMode: false,
      },
      editTransaction: {
        isEditTransactionMode: false,
        changedValue: [],
      },
    };
  };

  /**
   * 取引行メニューのStateを設定する関数
   * @param e - 選択された項目
   * @param business - 取引データ
   * @param virtualItemType - 行アイテムのタイプ
   * @param parentId - 子の場合親のID
   */
  const setBusinessRowMenuStateAndProcessing = async (
    e: 'create' | 'edit' | 'editTransaction' | 'delete',
    business: IBusiness,
    virtualItemType: 'loading' | 'error' | 'zeroMatch' | 'parent' | 'child',
    parentId?: string
  ) => {
    // 取引の種類を取得
    const businessType = ((virtualItemType: string): 'aggregateBusiness' | 'baseBusiness' | 'none' => {
      if (virtualItemType === 'parent') {
        return 'aggregateBusiness';
      } else if (virtualItemType === 'child') {
        return 'baseBusiness';
      } else {
        return 'none';
      }
    })(virtualItemType);

    // 数値データ編集
    if (e === 'editTransaction') {
      if (businessType === 'aggregateBusiness' && state.isOpeningBusinessId !== business.businessId) {
        state.isOpeningBusinessId = business.businessId;
        await waitLoading(fetchChildBusinesses(business.businessId), t('ローディング中'), '');
      } else if ((businessType === 'baseBusiness' && !parentId) || businessType === 'none') {
        await showInfoModal({
          modalTitle: t('エラーが発生しました'),
          modalDescription: t('時間をおいて再度お試しください。'),
          backButtonLabel: t('OK'),
          icon: 'info',
          enableOutsideClick: false,
          isDisabledNextButton: true,
        });
        return;
      }

      state.businessRowMenuState = {
        businessId: parentId ?? business.businessId,
        businessType: 'none',
        item: undefined,
        editTransaction: {
          isEditTransactionMode: true,
          changedValue: [],
        },
        edit: {
          isEditingModalOpen: false,
          isCreateMode: false,
        },
      };
      return;
    }

    // 対象取引の取得
    let itemList = business;
    if (virtualItemType === 'child' && parentId) {
      // 対象が子取引かつ、親のIDが存在する場合
      const parentBusiness = state.businessList.find((business) => business.businessId === parentId);
      if (parentBusiness) {
        // 親の取引が見つかった場合
        const {
          warehouseNumber,
          warehouse,
          supplierNumber,
          supplierName,
          businessNumber,
          businessName,
          package: parentPackage,
        } = parentBusiness;

        itemList = {
          ...itemList,
          warehouseNumber,
          warehouse,
          supplierNumber,
          supplierName,
          businessNumber,
          businessName,
          package: parentPackage,
        };
      }
    }
    const businessItemForState: Omit<BusinessRowMenuState, 'edit'> = {
      businessId: business.businessId,
      businessType: businessType,
      item: itemList,
      editTransaction: {
        isEditTransactionMode: false,
        changedValue: [],
      },
    };

    // Stateの決定とモーダル表示
    if (!e || businessItemForState.businessType === 'none') return;
    if (e === 'create') {
      // 取引新規作成時
      state.businessRowMenuState = {
        ...businessItemForState,
        edit: {
          isEditingModalOpen: true,
          isCreateMode: true,
        },
      };
    } else if (e === 'edit') {
      // 取引編集時
      state.businessRowMenuState = {
        ...businessItemForState,
        edit: {
          isEditingModalOpen: true,
          isCreateMode: false,
        },
      };
    } else if (e === 'delete') {
      // 取引削除時
      state.businessRowMenuState = {
        ...businessItemForState,
        edit: {
          isEditingModalOpen: false,
          isCreateMode: false,
        },
      };
      const confirmResult = await showInfoModal({
        modalTitle: t('選択した取引をゴミ箱へ移動します'),
        modalDescription: t('また、移動した取引は45日後に自動的に削除されますがよろしいですか？'),
        backButtonLabel: t('キャンセル'),
        nextButtonLabel: t('削除する'),
        icon: 'info',
        enableOutsideClick: false,
      });

      if (!confirmResult) {
        resetTableBusinessRowMenuState();
        return;
      }
      const deleteResult = await waitLoading(_onNextConfirmedDeleteBusiness(), t('削除中です・・・'), '');

      resetTableBusinessRowMenuState();
      if (deleteResult) {
        showInfoModal({
          modalTitle: t('削除に成功しました。'),
          backButtonLabel: t('OK'),
          icon: 'info',
          isDisabledNextButton: true,
          enableOutsideClick: false,
        });
        await fetchStateWithOpeningChildBusiness();
      } else {
        await showInfoModal({
          modalTitle: t('削除中にエラーが発生しました。再度お試しください。'),
          backButtonLabel: t('OK'),
          icon: 'error',
          colorStyle: 'error',
          isDisabledNextButton: true,
          enableOutsideClick: false,
        });
      }
    }
    return;
  };

  /**
   * 在庫管理ページの操作メニューのStateを設定する関数
   * @param e - 選択された項目
   */
  const setInventoryControlMenuStateAndProcessing = async (e: 'csvExport' | 'editDisplayRange' | 'combinedOrderSetting') => {
    // 受け取ったイベントに応じて処理を分岐させる
    if (e === 'csvExport') {
      // CSVエクスポートの処理
      await exportInventoryBusiness();
      return;
    } else if (e === 'editDisplayRange') {
      // 表示範囲編集のモーダルを開く
      state.selectDisplayInventoryYearMonthRangeModalState.isOpen = true;
      return;
    } else if (e === 'combinedOrderSetting') {
      state.combinedOrdersState.settingModalStatus = 'setting';
    }
    // それ以外のイベントは何もしない
    return;
  };

  /**
   * 数値編集の情報をputする
   */
  const putEditTransaction = async () => {
    const result = await waitLoading(
      diContainer.upsertTransactionValuesUseCase.Execute(
        state.businessRowMenuState.businessId,
        state.businessRowMenuState.editTransaction.changedValue
      ),
      t('数値データを保存中・・・'),
      ''
    );
    if (result?.status.type === 'success') {
      // 編集モードをリセット
      resetTableBusinessRowMenuState();
      // 開いているグループをリセット
      state.isOpeningBusinessId = '';
      // データを再取得
      await fetchState();
      showInfoModal({
        modalTitle: t('数値データの保存に成功しました'),
        backButtonLabel: t('OK'),
        icon: 'info',
        isDisabledNextButton: true,
        enableOutsideClick: false,
      });
      return;
    }
    await showInfoModal({
      modalTitle: t('数値データ保存中にエラーが発生しました。再度お試しください。'),
      backButtonLabel: t('OK'),
      icon: 'error',
      colorStyle: 'error',
      isDisabledNextButton: true,
      enableOutsideClick: false,
    });
    return;
  };

  /**
   * チェックボックスの選択状態を変更する
   * @param business - 取引情報
   * @param isChecked - チェックボックスの選択状態
   */
  const toggleCheckedRowData = (business: IParentBusiness | IBusiness, isChecked: boolean) => {
    if (isIParentBusiness(business)) {
      if (isChecked) {
        state.checkedAggregateBusinessIds.push(business.businessId);
      } else {
        state.checkedAggregateBusinessIds = state.checkedAggregateBusinessIds.filter((id) => id !== business.businessId);
      }
    }
    // TODO: 24-11-09 関連取引のチェックは未実装
  };

  /**
   * 選択した年月の在庫表を表示するモーダルのStateを設定する
   * @param selectedYearMonthInfo - 選択された年月情報
   */
  const setSelectDisplayInventoryYearMonthRangeModalState = async (selectedYearMonthInfo: YearMonthRange) => {
    state.selectDisplayInventoryYearMonthRangeModalState.isOpen = false;
    state.selectDisplayInventoryYearMonthRangeModalState.startYearMonth = selectedYearMonthInfo.startYearMonth;
    state.selectDisplayInventoryYearMonthRangeModalState.endYearMonth = selectedYearMonthInfo.endYearMonth;

    // preferenceに保存
    const yearMonthRangeInfoRepository = new YearMonthRangeInfoRepository();
    await yearMonthRangeInfoRepository.put('inventoryControl', {
      startYearMonth: selectedYearMonthInfo.startYearMonth,
      endYearMonth: selectedYearMonthInfo.endYearMonth,
    });

    // 月の距離を計算し直す
    state.transactionMonths = DateService.getMonthDistanceFromCurrentMonthList(
      state.selectDisplayInventoryYearMonthRangeModalState.startYearMonth,
      state.selectDisplayInventoryYearMonthRangeModalState.endYearMonth
    );
  };

  /**
   * まとめ発注をAPIに投げる
   */
  const onConfirmCombinedOrder = async () => {
    if (
      state.combinedOrdersState.combinedOrderContents.confirmedOrdersWithBusinessAndBasis.some(
        (orderWithBusiness) => orderWithBusiness.order.status === OrderStatus.ApprovalRequested
      )
    ) {
      const approvalRequestedOrderConfirmResult = await showInfoModal({
        modalTitle: t('承認依頼の発注が含まれています'),
        modalDescription: t('まとめ発注を作成すると含まれている承認依頼の発注も承認済みになりますがよろしいですか？'),
        nextButtonLabel: t('OK'),
        backButtonLabel: t('戻る'),
        enableOutsideClick: false,
        icon: 'info',
      });
      if (!approvalRequestedOrderConfirmResult) return;
    }
    const result = await waitLoading(
      diContainer.createCombinedOrderUseCase.execute(state.combinedOrdersState.combinedOrderContents),
      t('発注登録中・・・'),
      ''
    );
    if (!result) {
      await showInfoModal({
        modalTitle: t('発注登録中にエラーが発生しました。再度お試しください。'),
        backButtonLabel: t('OK'),
        icon: 'error',
        colorStyle: 'error',
        isDisabledNextButton: true,
        enableOutsideClick: false,
      });
      return;
    }
    switch (result.status.type) {
      case 'success':
        resetCombinedOrderStatus();
        state.checkedAggregateBusinessIds = [];
        await fetchState();
        return;
      case 'error':
        if (result.status.error.code === 'RESOURCE_CONFLICT') {
          await showInfoModal({
            modalTitle: t('まとめ発注（発注グループ）の名称が重複しています'),
            modalDescription: t('既存の名称と重複していないか確認してください。'),
            backButtonLabel: t('OK'),
            icon: 'error',
            colorStyle: 'error',
            isDisabledNextButton: true,
            enableOutsideClick: false,
          });
          return;
        } else if (result.status.error.code === 'UPDATE_TIMING_CONFLICT') {
          await showInfoModal({
            modalTitle: t('発注登録中にエラーが発生しました'),
            modalDescription: t('発注の更新時に他のユーザーと競合しました。リロードして再度お試しください。'),
            backButtonLabel: t('OK'),
            icon: 'error',
            colorStyle: 'error',
            isDisabledNextButton: true,
            enableOutsideClick: false,
          });
          return;
        }
      default:
        await showInfoModal({
          modalTitle: t('発注登録中にエラーが発生しました。再度お試しください。'),
          backButtonLabel: t('OK'),
          icon: 'error',
          colorStyle: 'error',
          isDisabledNextButton: true,
          enableOutsideClick: false,
        });
        return;
    }
  };

  /**
   * まとめ発注のStateをリセットする
   */
  const resetCombinedOrderStatus = () => {
    state.combinedOrdersState.combinedOrderContents = {
      groupName: '',
      orderCount: 0,
      totalAmount: 0,
      maxTotalAmount: 0,
      desiredDeliveryDate: {
        year: -1,
        month: -1,
        day: -1,
      },
      loadFactor: 0,
      confirmedOrdersWithBusinessAndBasis: [],
    };
    return;
  };

  /**
   * デフォルトの発注グループ名を作成する
   * @param orderCount - 発注の総数
   * @returns - デフォルトの発注グループ名
   */
  const createDefaultOrderGroupTitle = async (orderCount: number) => {
    const todayString = DateService.getTodayString();
    const tempGroupNameArgs = {
      nowDate: todayString,
      serialNumber: 1,
      orderCount: orderCount,
    };
    const keyword = t('デフォルトまとめ発注名接頭辞') + todayString;
    const getOrderGroupResult = await waitLoading(diContainer.getOrderGroupUseCase.execute(keyword));
    if (getOrderGroupResult?.status.type === 'success' && getOrderGroupResult.status.data.length > 0) {
      const maxSerialNumber = getOrderGroupResult.status.data
        .map((data) => {
          const checkRegexp = data.orderGroupName.match(/-(\d+)_/);
          return checkRegexp ? parseInt(checkRegexp[1]) : 0;
        })
        .reduce((max, num) => Math.max(max, num), 0);
      tempGroupNameArgs.serialNumber = maxSerialNumber + 1;
    }
    return t('まとめ発注_nowDate(yyyymmdd)-serialNumber_orderCount件', tempGroupNameArgs);
  };

  /**
   * 非表示のトランザクションヘッダーを取得する
   */
  const getHiddenTransactionHeader = async (): Promise<string[] | undefined> => {
    const result = await diContainer.getHiddenTableHeaderPreferenceUseCase.Execute(whichTableDisplay.value);
    if (result.status.type === 'error') {
      console.error('非表示ヘッダーの取得に失敗しました。');
      return undefined;
    }
    const hiddenHeader = result.status.data;
    const hiddenTransactionHeader = hiddenHeader.filter((header) => !vm.displayHeaders.inventoryControl.includes(header));
    state.hiddenTransactionHeaderList = hiddenTransactionHeader;
  };

  // #region private関数

  /**
   * TableDataをBusinessManagerから抽出する
   */
  const _getTableData = async (): Promise<IParentBusiness[] | null> => {
    // 期間選択された情報から、取得期間の情報を生成する
    const earlyPeriod = DateService.convertDateStrToDateWithHyphen(
      new Date(
        state.selectDisplayInventoryYearMonthRangeModalState.startYearMonth.year,
        state.selectDisplayInventoryYearMonthRangeModalState.startYearMonth.month - 1,
        1
      )
    );
    const latterPeriod = DateService.convertDateStrToDateWithHyphen(
      new Date(
        state.selectDisplayInventoryYearMonthRangeModalState.endYearMonth.year,
        state.selectDisplayInventoryYearMonthRangeModalState.endYearMonth.month - 1,
        1
      )
    );

    const useFilterItems = [...cachedFilterItems.value];

    if (state.editGroupModeState.selectedEditGroupId !== '') {
      useFilterItems.push(GroupMenuService.getFilterContentItem(state.editGroupModeState.selectedEditGroupId));
    }

    // state.headerSortingState.key をバックエンドのカラム名に変換
    let convertedSortKey: string;
    if (InventoryTableService.isTransactionSortKey(state.headerSortingState.key)) {
      // 現在庫(nowInventory)の場合はExecutionDateから年月を取得する必要がある
      const executionDate = state.businessList[0]?.executionDate;
      convertedSortKey = InventoryTableService.convertTransactionKeyToSortParameter(state.headerSortingState.key, executionDate);
    } else {
      convertedSortKey = InventoryTableService.convertHeaderKeyToParameter(state.headerSortingState.key);
    }

    // loading中はデータを取得するまで待機する
    const result = await diContainer.getInventoryUseCase.execute(
      state.headerSortingState.isAsc ? 'ASC' : 'DESC',
      state.pagingOptions.pageSize,
      state.pagingOptions.offset,
      earlyPeriod,
      latterPeriod,
      convertedSortKey,
      useFilterItems
    );

    switch (result.status.type) {
      case 'success':
        const [businesses, summaryData, pagingOptions] = result.status.data;

        const businessArray = simpleCopy(businesses);
        const simulateResult = await GetRecommendedOrderForInventoryControlUseCase.Execute(businessArray);
        switch (simulateResult.status.type) {
          case 'success':
            // 集計行を生成する
            state.summaryData = summaryData;
            state.pagingOptions = pagingOptions;
            return simulateResult.status.data;
          case 'error':
          default:
            state.tableLoadingState.status.type = 'error';
            return null;
        }
      case 'error':
      default:
        state.tableLoadingState.status.type = 'error';
        return null;
    }
  };

  /**
   * 取引削除確認モーダル確認後の処理
   */
  const _onNextConfirmedDeleteBusiness = async () => {
    // 取引削除処理
    let result: INetworkResult<UpdateTrashBinAggregateBusinessSuccessResponseInfo | UpdateTrashBinBusinessSuccessResponseInfo, IError>;

    if (state.businessRowMenuState.businessType === 'aggregateBusiness') {
      result = await diContainer.deleteDeletionAggregateBusinessUseCase.Execute([state.businessRowMenuState.businessId]);
    } else if (state.businessRowMenuState.businessType === 'baseBusiness') {
      result = await diContainer.deleteDeletionBaseBusinessUseCase.Execute([state.businessRowMenuState.businessId]);
    } else {
      // 基本ここには来ない、なんらかの原因で取引が正しく選択されていない場合
      return false;
    }

    // 後処理
    switch (result.status.type) {
      case 'success':
        return true;
      case 'error':
      default:
        return false;
    }
  };

  /**
   * state変数内の表示年月範囲情報を初期化する
   */
  const _initSelectedYearMonthRange = async () => {
    // 表示年月範囲情報の取り出しおよびstate変数への設定
    const selectedYearMonthRangeInfo = await initStartAndEndYearMonth();
    state.selectDisplayInventoryYearMonthRangeModalState.startYearMonth = selectedYearMonthRangeInfo.startYearMonth;
    state.selectDisplayInventoryYearMonthRangeModalState.endYearMonth = selectedYearMonthRangeInfo.endYearMonth;
    state.transactionMonths = DateService.getMonthDistanceFromCurrentMonthList(
      selectedYearMonthRangeInfo.startYearMonth,
      selectedYearMonthRangeInfo.endYearMonth
    );
  };

  // #endregion

  // #region 内示正答率関連アクション

  /**
   * 設定内示正答率を更新する（揮発性）
   * @param businessId - 取引ID
   * @param accuracy - 正答率
   */
  const updateForecastAccuracySettingAccuracy = (businessId: string, accuracy: number | undefined) => {
    // 範囲チェック（0〜200%）
    if (accuracy !== undefined && (accuracy < 0 || accuracy > 200)) {
      toast.error(t('設定内示正答率は0〜200の範囲で入力してください'));
      return;
    }

    if (!state.forecastAccuracyData[businessId]) {
      state.forecastAccuracyData[businessId] = { businessId };
    }
    state.forecastAccuracyData[businessId].settingAccuracy = accuracy;
  };

  /**
   * 内示正答率履歴モーダルを開く
   * @param businessId - 取引ID
   */
  const openForecastAccuracyHistoryModal = async (businessId: string) => {
    state.forecastAccuracyModalState.isOpen = true;
    state.forecastAccuracyModalState.targetBusinessId = businessId;
    state.forecastAccuracyModalState.loadingState = {
      type: 'loading',
    };
    state.forecastAccuracyModalState.historyData = undefined;

    // キャッシュされた生データがあるかチェック
    const cachedRawData = rawDataCache[businessId];

    if (cachedRawData) {
      // キャッシュされたデータを使用してクライアントサイドでフィルタリング（デフォルト期間: 1）
      try {
        const historyData = ForecastAccuracyAdapter.adaptToForecastAccuracyHistory(cachedRawData, 1);
        state.forecastAccuracyModalState.historyData = historyData;
        state.forecastAccuracyModalState.loadingState = {
          type: 'success',
        };
      } catch (error) {
        state.forecastAccuracyModalState.loadingState = {
          type: 'error',
          errorMessage: 'データの変換に失敗しました',
        };
      }
      return;
    }

    try {
      // 内示正答率履歴データを取得（生データのみ）
      const result = await diContainer.getForecastAccuracyHistoryUseCase.execute(businessId);
      if (result.status.type === 'success') {
        const rawData = result.status.data;

        // 生データをキャッシュに保存
        rawDataCache[businessId] = rawData;

        // 生データから指定期間のビューモデルを作成（デフォルト期間: 1）
        const historyData = ForecastAccuracyAdapter.adaptToForecastAccuracyHistory(rawData, 1);
        state.forecastAccuracyModalState.historyData = historyData;
        state.forecastAccuracyModalState.loadingState = {
          type: 'success',
        };
      } else {
        // エラー時はエラーメッセージをセット
        state.forecastAccuracyModalState.loadingState = {
          type: 'error',
          errorMessage: '履歴の取得に失敗しました',
        };
      }
    } catch (error) {
      state.forecastAccuracyModalState.loadingState = {
        type: 'error',
        errorMessage: '履歴の取得に失敗しました',
      };
    }
  };

  /**
   * 内示正答率履歴モーダルを閉じる
   */
  const closeForecastAccuracyHistoryModal = () => {
    state.forecastAccuracyModalState.isOpen = false;
    state.forecastAccuracyModalState.targetBusinessId = undefined;
    state.forecastAccuracyModalState.historyData = undefined;
    state.forecastAccuracyModalState.loadingState = {
      type: 'zero-match',
    };
  };

  /**
   * 履歴モーダルの期間を変更して再取得する
   * @param period - 期間（1-3ヶ月）
   * @description キャッシュされた生データを使用してクライアントサイドでフィルタリング
   */
  const changeForecastAccuracyHistoryPeriod = async (period: 1 | 2 | 3) => {
    if (!state.forecastAccuracyModalState.targetBusinessId) return;

    // キャッシュされた生データがあるかチェック
    const cachedRawData = rawDataCache[state.forecastAccuracyModalState.targetBusinessId];

    if (cachedRawData) {
      // キャッシュされたデータを使用してクライアントサイドでフィルタリング
      try {
        const historyData = ForecastAccuracyAdapter.adaptToForecastAccuracyHistory(cachedRawData, period);
        state.forecastAccuracyModalState.historyData = historyData;
        state.forecastAccuracyModalState.loadingState = {
          type: 'success',
        };
      } catch (error) {
        state.forecastAccuracyModalState.loadingState = {
          type: 'error',
          errorMessage: 'データの変換に失敗しました',
        };
      }
      return;
    }

    // キャッシュにデータがない場合はAPIから取得
    state.forecastAccuracyModalState.loadingState = {
      type: 'loading',
    };

    try {
      const result = await diContainer.getForecastAccuracyHistoryUseCase.execute(state.forecastAccuracyModalState.targetBusinessId);
      if (result.status.type === 'success') {
        const rawData = result.status.data;

        // 生データをキャッシュに保存
        rawDataCache[state.forecastAccuracyModalState.targetBusinessId] = rawData;

        // 生データから指定期間のビューモデルを作成
        const historyData = ForecastAccuracyAdapter.adaptToForecastAccuracyHistory(rawData, period);
        state.forecastAccuracyModalState.historyData = historyData;
        state.forecastAccuracyModalState.loadingState = {
          type: 'success',
        };
      } else {
        state.forecastAccuracyModalState.loadingState = {
          type: 'error',
          errorMessage: '履歴の取得に失敗しました',
        };
      }
    } catch (error) {
      state.forecastAccuracyModalState.loadingState = {
        type: 'error',
        errorMessage: '履歴の取得に失敗しました',
      };
    }
  };

  // #endregion

  return {
    state,
    $reset,
    initTableColumnsWidth,
    toggleOpenBusinesses,
    fetchState,
    refreshBusiness,
    onChangePaging,
    fetchChildBusinesses,
    updateMemoText,
    switchTableMode,
    setNormalMode,
    confirmOrderEdit,
    closeWorkDaysCalendarNotYetEnteredAlertModal,
    onAppliedFilterModal,
    onToggleDisplayHeader,
    onCheckedAllRows,
    onSelectedGroupMenu,
    onClosedGroupActionModal,
    onAppliedCreateAggregateBusinessGroup,
    onAppliedAddAggregateBusinessGroup,
    onAppliedEditAggregateBusinessGroup,
    onAppliedDeleteAggregateBusinessGroup,
    onAppliedUpdateNameAggregateBusinessGroup,
    onSwitchedEditTargetGroup,
    onClickedDeleteFromGroupButton,
    onBackToNormalMode,
    getTableAlertData,
    toggleOpenAlert,
    setupMoveWarehouse,
    applyMoveWarehouse,
    changeHeaderSorting,
    getIsTransactionHeaderDisplay,
    exportInventoryBusiness,
    toggleCheckedRowData,
    setSelectDisplayInventoryYearMonthRangeModalState,
    setInventoryControlMenuStateAndProcessing,
    getHiddenTransactionHeader,

    // 内示正答率関連のアクション
    updateForecastAccuracySettingAccuracy,
    openForecastAccuracyHistoryModal,
    closeForecastAccuracyHistoryModal,
    changeForecastAccuracyHistoryPeriod,

    fetchStateWithOpeningChildBusiness,
    resetTableBusinessRowMenuState,
    setBusinessRowMenuStateAndProcessing,
    putEditTransaction,

    onConfirmCombinedOrder,
    resetCombinedOrderStatus,
    createDefaultOrderGroupTitle,
    getFilterItemsValue,
    updateUnpaidOrderedOrder,
  };
});

/**
 * storeの型定義
 */
export type UseInventoryStore = ReturnType<typeof useInventoryStore>;
